import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { PersonalData } from '@/shared/api/protocol-ts/model/dto_base_pb';
import { SAFE_AVATAR_IMAGE_FORMATS } from '@/shared/config/constants';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { getImageSrc, validateImageFile } from '@/shared/lib';
import { Icon, Input, Modal, UploadImageInput } from '@/shared/ui';

import { assetsModel } from '@/entities/assets';
import { ModalID, modalModel } from '@/entities/modal';
import { userModel } from '@/entities/user';

import { useUploadAssetContext } from '@/features/uploadAsset';

import { EditInfoFormPayload, editInfoFormSchema } from '../config/formSchema';

import styles from './EditInfoModal.module.scss';

export type EditInfoModalProps = {
  userID: string;
  className?: string;
};

export const EditInfoModal: FC<EditInfoModalProps> = ({
  userID,
  className,
}) => {
  const [photo, setPhoto] = useState<FileWithPath>();
  const [isUserAvatarRemoved, setIsUserAvatarRemoved] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string>('');

  const dispatch = useAppDispatch();
  const { visible } = useAppSelector((state) => state.modal.EditInfoModal);

  const { formatMessage } = useIntl();

  const user = useAppSelector(userModel.selectors.selectCurrentUser);

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.EditInfoModal));
  }, []);

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<EditInfoFormPayload>({
    resolver: yupResolver(editInfoFormSchema),
  });

  const getEditUserDefaultValues = (userInfo: PersonalData) => ({
    firstName: userInfo.FirstName,
    lastName: userInfo.LastName,
  });

  const handelChangePhoto = (image: FileWithPath) => {
    const isImageInvalid = validateImageFile(
      image,
      (message) => setPhotoUploadError(message),
      formatMessage,
    );

    if (isImageInvalid) {
      return;
    }

    setPhotoUploadError('');
    setPhoto(image);
  };

  const { startUploadAsset: startUploadStudy } = useUploadAssetContext();

  const onSubmit: SubmitHandler<EditInfoFormPayload> = async (data) => {
    const { firstName, lastName } = data;

    try {
      if (!isEmpty(photo)) {
        await startUploadStudy(
          {
            files: [photo],
            userID: user?.ID,
            assetType: AssetType.AssetType_User_Avatar,
            meta: {
              studyType: AssetType.AssetType_User_Avatar,
              patientName: user?.PersonalData?.FirstName,
            },
          },
          true,
        );
      } else if (isUserAvatarRemoved) {
        await dispatch(
          assetsModel.thunks.deleteAsset({ AssetID: user.AvatarAssetID }),
        );
      }

      const { User } = await dispatch(
        userModel.thunks.setUserName({
          UserID: userID,
          FirstName: firstName,
          LastName: lastName,
        }),
      ).unwrap();

      if (User) {
        dispatch(userModel.actions.setOne(User));
      }
    } finally {
      handleClose();
    }
  };

  const userAvatarSrc = getImageSrc(user?.AvatarAssetID, 'thumbnail');

  const handleRemoveUserAvatar = () => {
    if (user.AvatarAssetID) {
      setIsUserAvatarRemoved(true);
    }
  };

  // Clear error and reset form when modal opens
  useEffect(() => {
    if (visible) {
      setPhotoUploadError('');
      if (user.PersonalData) {
        reset(getEditUserDefaultValues(user.PersonalData));
      }
    }
  }, [visible, user, reset]);

  const hasImageInUploadInput =
    !isEmpty(photo) || (userAvatarSrc && !isUserAvatarRemoved);

  return (
    <Modal
      data-component="edit-info-modal"
      title={formatMessage({
        id: 'editInfo.EditPersonalInfo',
        defaultMessage: 'Edit personal info',
      })}
      isOpen={visible}
      onCancel={handleClose}
      className={cn(styles.modal, className)}
      containerClassName={styles.modalContainer}
      borderless
      okButtonText={formatMessage({
        id: 'global.save',
        defaultMessage: 'Save',
      })}
      okButtonProps={{
        type: 'submit',
        form: 'editInfoForm',
        loading: isSubmitting,
      }}
    >
      <form
        id="editInfoForm"
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.dragDescription}>
          <UploadImageInput
            className={cn(
              styles.uploadImage,
              hasImageInUploadInput && styles.hasImage,
            )}
            image={photo as FileWithPath}
            onChange={handelChangePhoto}
            accept={SAFE_AVATAR_IMAGE_FORMATS}
            hideImageDescription
            previewImage={userAvatarSrc}
            dropDescription={
              <Icon name="camera" className={styles.purpleText} size={24} />
            }
            onRemove={handleRemoveUserAvatar}
          />
          <div>
            <p className={cn(styles.purpleText, 'p2')}>
              <FormattedMessage
                id="editInfo.AddYourImage"
                defaultMessage="Add your image"
              />
            </p>

            <p className={cn(styles.formatsText, 'p3')}>
              <FormattedMessage
                id="editInfo.imageFormats"
                defaultMessage="max 2 MB, file formats: JPG, PNG, JPEG"
              />
            </p>

            {photoUploadError && (
              <p className={cn(styles.errorText, 'p3')}>{photoUploadError}</p>
            )}
          </div>
        </div>

        <Controller
          control={control}
          name="firstName"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              required
              ref={ref}
              value={value}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              type="text"
              label={formatMessage({
                id: 'editInfo.firstName',
                defaultMessage: 'First name',
              })}
              error={error?.message}
              autoComplete="given-name"
            />
          )}
        />

        <Controller
          control={control}
          name="lastName"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              className={styles.inputLastName}
              required
              ref={ref}
              value={value}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              type="text"
              label={formatMessage({
                id: 'editInfo.lastName',
                defaultMessage: 'Last name',
              })}
              error={error?.message}
              autoComplete="family-name"
            />
          )}
        />
      </form>
    </Modal>
  );
};
